import { DownOutlined, UploadOutlined } from '@ant-design/icons';
import { Image2FilledIcon, SailBoatIcon } from '@marine/icons';
import { Button, Dropdown, Space } from 'antd';
import cn from 'classnames';
import React, { FC, useCallback, useMemo } from 'react';
import { Image } from 'sailor-ui';
import { useFilePicker } from '../../picker/filePicker.context';
import { pickerFileFromGalleryFile } from '../../picker/galleryPickerFiles';
import styles from './CoverImage.module.scss';

export type CoverImageProps = {
  /**
   * Text displayed at the bottom of the card besides buttons
   */
  title?: string | null;
  /**
   * No cover image title
   */
  noCoverTitle?: string;
  /**
   * Class name to add to container element
   */
  className?: string;
  readOnly?: boolean;
  coverImage?: any;
  id?: string;
  disabled?: boolean;
  bottomComponent?: React.ReactNode;
  onChange?: (value?: any) => void;
  onFinish?: (value?: any) => void;
};

export const CoverImage: FC<CoverImageProps> = ({
  title,
  noCoverTitle = 'Select custom cover image',
  className,
  coverImage,
  readOnly,
  onChange = (_value: any) => {},
  disabled,
  bottomComponent,
  onFinish,
}) => {
  const pickFiles = useFilePicker();
  const initialValue = useMemo(() => (coverImage ? { ...coverImage, type: 'image' } : undefined), [coverImage]);

  /**
   * The current cover as the picker's seed, or undefined when it has no backend id to match on.
   *
   * A cover loaded from GraphQL carries the backend id as `id` and has no `laravel_id` at all, so
   * reading that field directly only ever seeded a cover picked earlier in the same session. A
   * Sanity cover has neither, and is dropped — its asset ref is not a file the picker can list.
   */
  const initialFile = useMemo(() => pickerFileFromGalleryFile(initialValue), [initialValue]);

  /**
   * Opens the app's picker and takes the first image chosen.
   *
   * Images only and one at a time, which the picker enforces itself: `accept` narrows the files it
   * lists while leaving every folder browsable, and single mode makes a second pick replace the
   * first. Cancelling resolves undefined and leaves the cover exactly as it was.
   */
  const handleChangeCover = useCallback(async () => {
    const picked = await pickFiles({
      title: 'Change cover image',
      accept: ['image'],
      multiple: false,
      initialFiles: initialFile ? [initialFile] : undefined,
    });

    const cover = picked?.at(0);

    if (cover) {
      onChange(cover);
      onFinish?.(cover);
    }
  }, [pickFiles, initialFile, onChange, onFinish]);

  const handleRemoveCover = useCallback(() => {
    onChange(null);
    onFinish?.(null);
  }, [onChange, onFinish]);

  const titleMemo = useMemo(
    () => (
      <div className={styles.titleContainer}>
        <span className={styles.footerText}>{title}</span>
      </div>
    ),
    [title],
  );

  return (
    <div className={styles.container}>
      {coverImage?.id ? (
        <>
          <div className={cn(styles.imageWrapper, className)}>
            <Image imageId={coverImage?.id} alt="Cover" className={styles.image} format="cardLg" />
            {!disabled && (
              <div className={styles.dropdown}>
                <Space.Compact>
                  <Button disabled={readOnly} icon={<Image2FilledIcon />} onClick={handleChangeCover}>
                    Change
                  </Button>
                  <Dropdown menu={{ items: [{ key: '1', label: 'Remove', danger: true, onClick: handleRemoveCover }] }}>
                    <Button icon={<DownOutlined />} disabled={readOnly} />
                  </Dropdown>
                </Space.Compact>
              </div>
            )}
          </div>
          {title && <div className={styles.footerShadow}>{titleMemo}</div>}
        </>
      ) : (
        <>
          <div className={cn(styles.imageWrapper, styles.placeholder, className)}>
            <div className={styles.iconContainer}>
              <SailBoatIcon />
            </div>

            <div className={styles.subtitle}>{noCoverTitle || 'No image available...'}</div>

            {!disabled && (
              <Button
                onClick={handleChangeCover}
                icon={<UploadOutlined />}
                className={styles.uploadBtn}
                disabled={readOnly}
              >
                Select image
              </Button>
            )}
          </div>

          {title && titleMemo}
        </>
      )}

      {bottomComponent && <div className={styles.bottomComponent}>{bottomComponent}</div>}
      {!bottomComponent && title && <div className={styles.bottomComponent}>{titleMemo}</div>}
    </div>
  );
};
